

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '@/shared/components/LodingSpiner/LodingSpiner';
import CategoryCard from '../../../../shared/components/ProductCardsE/ProductCardsE';
import ProductCard from '../../../../shared/components/ProductCardsA/ProductCard';
import ProductCardMobile from '../../../../shared/components/ProductCardsA/ProductCardMobile';
import categoryTreeService, { CategoryTreeNode } from '../../services/categoryTree.service';
import productService, { Product } from '../../services/product.service';
import { useCart } from '../../../../contexts/CartContext';
import './styles/ViewAllCards.css';
import './styles/ViewAllCards.mobile.css';


const ViewAllCards: React.FC = () => {
  const [categories, setCategories] = useState<CategoryTreeNode[]>([]);
  const [categoryProducts, setCategoryProducts] = useState<Record<string, Product[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const { addToCart } = useCart();

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth < 768);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await categoryTreeService.getCategoryTree();
      if (response.success && response.data) {
        setCategories(response.data);
        const productFetches = response.data.map(async (category: CategoryTreeNode) => {
          if (!category.children || category.children.length === 0) {
            try {
              const prodRes = await productService.getProductsByCategory(category.slug);
              if (prodRes.success && prodRes.data) {
                return { slug: category.slug, products: prodRes.data };
              }
            } catch {}
          }
          return null;
        });
        const productsResults = await Promise.all(productFetches);
        const productsMap: Record<string, Product[]> = {};
        productsResults.forEach((result) => {
          if (result && result.slug) {
            productsMap[result.slug] = result.products;
          }
        });
        setCategoryProducts(productsMap);
      } else {
        setError('Failed to load categories');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubcategoryClick = (slug: string) => {
    navigate(`/view-products/${slug}`);
  };

  const handleProductAddToCart = async (product: Product, imgSrc: string) => {
    const cartProduct = {
      id: product._id,
      _id: product._id,
      title: product.title,
      slug: product.slug,
      price: product.price || 0,
      imageUrl: imgSrc,
    } as any;

    await addToCart(cartProduct, 1);
  };

  const renderProductCard = (product: Product, truncatedTitle: string, imgSrc: string) => {
    const priceLabel = product.price ? `₹${Math.round(product.price)}` : '';

    if (isMobile) {
      return (
        <ProductCardMobile
          key={product._id}
          id={product._id}
          title={truncatedTitle}
          price={priceLabel}
          image={imgSrc}
          slug={product.slug}
          deliveryTag={true}
          onAddToCart={() => handleProductAddToCart(product, imgSrc)}
        />
      );
    }

    return (
      <ProductCard
        key={product._id}
        id={product._id}
        title={truncatedTitle}
        price={priceLabel}
        image={imgSrc}
        slug={product.slug}
        deliveryTag={true}
        onAddToCart={() => handleProductAddToCart(product, imgSrc)}
      />
    );
  };

  if (loading) {
    return (
      <div className="view-all-cards-container flex justify-center items-center min-h-[300px]">
        <LoadingSpinner size="large" />
      </div>
    );
  }
  if (error) {
    return <div className="view-all-cards-container"><div className="error-message">{error}</div></div>;
  }

  return (
    <div className="view-all-cards-container">
      {categories.map((category) => (
        <div key={category._id} className="view-all-section">
          <div className="section-header">
            <h2 className="section-heading">{category.name}</h2>
            {category.description && (
              <p className="section-description">{category.description}</p>
            )}
          </div>
          <div className="categories-products-grid">
            {category.children && category.children.length > 0 ? (
              category.children.map((sub) => (
                <div
                  key={sub._id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSubcategoryClick(sub.slug)}
                >
                  <CategoryCard
                    id={sub._id}
                    title={sub.name}
                    imageSrc={sub.image || 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868'}
                    slug={sub.slug}
                  />
                </div>
              ))
            ) : (
              categoryProducts[category.slug] && categoryProducts[category.slug].length > 0 ? (
                categoryProducts[category.slug].slice(0, 10).map((product) => {
                  const truncatedTitle = product.title.split(' ').slice(0, 4).join(' ');
                  let imgSrc = product.imageUrl;
                  if (!imgSrc && Array.isArray(product.images) && product.images.length > 0 && product.images[0].url) {
                    imgSrc = product.images[0].url;
                  }
                  if (!imgSrc) {
                    imgSrc = 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868';
                  }
                  return renderProductCard(product, truncatedTitle, imgSrc);
                })
              ) : (
                <div className="no-subcategories">No subcategories or products</div>
              )
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ViewAllCards;
